import { useAuth0 } from '@auth0/auth0-react';
import { User } from '../types';
import { getConfig } from '../config';
import { tokenService } from '../services/tokenService';
import { authStateTracking } from '../utils/sessionStorage';

export const useAuth0Service = () => {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    getIdTokenClaims,
    user,
    error
  } = useAuth0();
  
  const login = async (returnTo?: string, screenHint?: 'signup' | 'login') => {
    // Check if authentication is already in progress to prevent loops
    if (authStateTracking.isAuthInProgress()) {
      console.log('Auth0Service: Authentication already in progress, skipping login');
      return;
    }
    
    // Set authentication in progress flag
    authStateTracking.setAuthInProgress();
    
    // If returnTo is provided, use it for redirection after authentication
    console.log('Auth0Service: Initiating login with returnTo:', returnTo || 'none', 'screenHint:', screenHint || 'none');
    await loginWithRedirect({
      appState: returnTo ? { returnTo } : undefined,
      authorizationParams: screenHint ? {
        screen_hint: screenHint
      } : undefined
    });
  };
  
  const logoutUser = () => {
    // Clear all tokens
    tokenService.clearTokens();
    
    // Clear authentication in progress flag
    authStateTracking.clearAuthInProgress();
    
    const { logoutUrl } = getConfig().auth0 || { logoutUrl: window.location.origin };
    logout({ logoutParams: { returnTo: logoutUrl } });
  };
  
  // Get both access token and ID token
  const getTokens = async () => {
    try {
      console.log('Auth0Service: Getting tokens silently...');
      
      // Get access token
      const accessToken = await getAccessTokenSilently();
      console.log('Auth0Service: Access token received:', accessToken ? 'Token exists' : 'No token received');
      console.log('Auth0Service: Access token:', accessToken);

      // Get ID token claims
      const idTokenClaims = await getIdTokenClaims();
      console.log('Auth0Service: ID token claims received:', idTokenClaims ? 'Claims exist' : 'No claims received');
      console.log('Auth0Service: ID token claims:', idTokenClaims);

      // Extract raw ID token from claims
      const idToken = idTokenClaims?.__raw;
      
      // Register both tokens with our token service
      if (accessToken && idToken) {
        console.log('Auth0Service: Registering both tokens with tokenService');
        tokenService.registerTokens(accessToken, idToken);
        
        // Verify the tokens were properly stored
        const storedAccessToken = tokenService.getAccessToken();
        const storedIdToken = tokenService.getIdToken();
        
        console.log('Auth0Service: Verified stored access token:',
          storedAccessToken ? 'Token stored successfully' : 'Token storage failed');
        console.log('Auth0Service: Verified stored ID token:',
          storedIdToken ? 'Token stored successfully' : 'Token storage failed');
          
        return { accessToken, idToken };
      } else {
        console.warn('Auth0Service: One or both tokens missing');
        return {
          accessToken: accessToken || null,
          idToken: idToken || null
        };
      }
    } catch (error) {
      console.error('Auth0Service: Error getting tokens:', error);
      throw error;
    }
  };
  
  // Legacy method for backward compatibility
  const getToken = async () => {
    try {
      const { accessToken } = await getTokens();
      return accessToken;
    } catch (error) {
      console.error('Auth0Service: Error in legacy getToken method:', error);
      throw error;
    }
  };
  
  const getCurrentUser = (): User | null => {
    if (!user) return null;
    
    const currentDate = new Date().toISOString();
    
    // Check for admin role in the roles claim
    let isAdmin = false;
    const rolesClaim = user['https://veripay.com.au/roles'];
    // Log the roles claim for debugging
    console.log('Auth0Service: Roles claim for user:', rolesClaim);
    console.log('Auth0Service: User:', user);
    if (rolesClaim && Array.isArray(rolesClaim)) {
      isAdmin = rolesClaim.includes('app-admin');
    }
    
    // Create a basic user object from Auth0 data
    // The sellerId and hasSetSellerId properties will be updated from the backend API
    // in the refreshCurrentUser method in AuthContext
    return {
      id: user.sub || '',
      email: user.email || '',
      name: user.name || '',
      hasSetUserId: false, // Default to false, will be updated from API
      userId: undefined,   // Will be updated from API if it exists
      isAdmin: isAdmin,    // Add admin role flag
      createdAt: user.updated_at || currentDate,
      updatedAt: currentDate
    };
  };
  
  // Get user roles from the backend API
  const getUserRoles = async (): Promise<string[]> => {
    try {
      // Get access token for API call
      const token = await getToken();
      
      // Call the roles endpoint
      const response = await fetch(`${getConfig().api.baseURL}/roles`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching roles: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.roles || [];
    } catch (error) {
      console.error('Error getting user roles:', error);
      return [];
    }
  };
  
  // Initialize tokens on service creation
  const initializeTokens = async () => {
    console.log('Auth0Service: Initializing tokens');
    if (isAuthenticated && !isLoading) {
      try {
        console.log('Auth0Service: User is authenticated, getting tokens');
        await getTokens();
        
        // Clear authentication in progress flag since we're now authenticated
        authStateTracking.clearAuthInProgress();
      } catch (error) {
        console.error('Auth0Service: Error initializing tokens:', error);
      }
    } else {
      console.log('Auth0Service: User not authenticated or still loading, skipping token initialization');
    }
  };
  
  // Call initialize tokens when the service is created
  if (isAuthenticated && !isLoading) {
    console.log('Auth0Service: Attempting to initialize tokens on service creation');
    initializeTokens();
  }
  
  return {
    isAuthenticated,
    isLoading,
    login,
    logout: logoutUser,
    getTokens,
    getToken, // Legacy method
    getCurrentUser,
    getUserRoles, // Add this method
    error,
    initializeTokens
  };
};

export default useAuth0Service;